/*
 * Panel perspectives (night-run PER-01): four readings of the same route `/`, chosen by `?perspectiva=` (ND-14), so
 * no screen is duplicated and the server keeps deciding access on every request. Which perspective opens first for
 * each role is D-38, a proposal: until it is accepted the default is labelled provisional on screen.
 */

import type { CSSProperties } from 'react';

export type PerspectiveId = 'executiva' | 'financeiro' | 'qualidade' | 'sistema';

export const PERSPECTIVE_PARAM = 'perspectiva';

/** Decision that sets the default perspective per role (docs/03-decision-register.md). */
export const DEFAULT_RULE = { decision: 'D-38', homologated: false } as const;

export interface Perspective {
  id: PerspectiveId;
  label: string;
  /** One sentence under the tabs: what this reading is for. */
  purpose: string;
}

export const PERSPECTIVES: readonly Perspective[] = [
  {
    id: 'executiva',
    label: 'Executiva',
    purpose: 'Receita, fluxo, conciliação, resultado e tendências do mês para a gestão.',
  },
  {
    id: 'financeiro',
    label: 'Controle Financeiro',
    purpose:
      'Caixa, depósitos, conciliação, meios de pagamento, DRE e fechamentos para o controle financeiro.',
  },
  {
    id: 'qualidade',
    label: 'Qualidade dos Dados',
    purpose: 'Portões de qualidade, fechamentos pendentes, pendências e alertas da operação.',
  },
  {
    id: 'sistema',
    label: 'Saúde do Sistema',
    purpose: 'Situação técnica do sistema, alertas técnicos e pendências da administração.',
  },
];

interface UnitLike {
  roles?: readonly string[];
  permissions: readonly string[];
}
interface SessionLike {
  units: readonly UnitLike[];
}

/**
 * Perspectives the session may open in the selected unit, in display order. The panel figures need dashboard.read
 * in the unit (the same check as GET /reports/dashboard and /quality/indicators); the financial reading also needs
 * finance.read. Saúde do Sistema reads /v1/ops, which the server grants through system.health.read in any unit
 * (ADMIN has no dashboard.read, 0007_rbac_reference.sql).
 */
export function allowedPerspectives(
  session: SessionLike | null,
  unit: UnitLike | undefined,
): PerspectiveId[] {
  const inUnit = (p: string) => Boolean(unit?.permissions.includes(p));
  const anywhere = (p: string) => Boolean(session?.units.some((u) => u.permissions.includes(p)));
  const out: PerspectiveId[] = [];
  if (inUnit('dashboard.read')) out.push('executiva');
  if (inUnit('dashboard.read') && inUnit('finance.read')) out.push('financeiro');
  if (inUnit('dashboard.read')) out.push('qualidade');
  if (anywhere('system.health.read')) out.push('sistema');
  return out;
}

/** D-38 (proposal), in order: a user with several roles opens the perspective of the first role listed here. */
const ROLE_DEFAULT: readonly (readonly [string, PerspectiveId])[] = [
  ['GESTOR', 'executiva'],
  ['FINANCEIRO', 'financeiro'],
  ['CONFERENTE', 'financeiro'],
  ['OPERADOR', 'qualidade'],
  ['ADMIN', 'sistema'],
];

/** Default perspective for the roles in the unit, or the first allowed one when the role's default is not. */
export function defaultPerspective(
  roles: readonly string[] | undefined,
  allowed: readonly PerspectiveId[],
): PerspectiveId | null {
  for (const [role, id] of ROLE_DEFAULT) {
    if (roles?.includes(role) && allowed.includes(id)) return id;
  }
  return allowed[0] ?? null;
}

const isPerspective = (v: string | null): v is PerspectiveId => PERSPECTIVES.some((p) => p.id === v);

/**
 * Perspective to show: the one in the address when the session may open it, otherwise the role's default. An
 * address with a perspective the profile cannot open falls back silently; the server refuses the data anyway.
 */
export function resolvePerspective(
  requested: string | null,
  roles: readonly string[] | undefined,
  allowed: readonly PerspectiveId[],
): { id: PerspectiveId | null; isDefault: boolean } {
  if (isPerspective(requested) && allowed.includes(requested)) {
    return { id: requested, isDefault: requested === defaultPerspective(roles, allowed) };
  }
  return { id: defaultPerspective(roles, allowed), isDefault: true };
}

export const perspectiveLabel = (id: PerspectiveId): string =>
  PERSPECTIVES.find((p) => p.id === id)?.label ?? id;

/** Drill-down links of the perspective blocks are text links; on a phone they still need a 44 px target. */
export const DRILL_LINK: CSSProperties = { display: 'inline-flex', alignItems: 'center', minHeight: 44 };

/** Permissions that open at least one source of the pending-items centre (canReadCentre in tasks/projection.ts). */
const CENTRE_SOURCES = [
  'exception.read',
  'alert.read',
  'finance.read',
  'movement.read',
  'user.manage',
  'task.manage',
];
export const canSeePending = (unit: UnitLike | undefined): boolean =>
  CENTRE_SOURCES.some((p) => unit?.permissions.includes(p));
